import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Form, message, Tag } from 'antd';
import { StarFilled } from '@ant-design/icons';
import { format } from 'date-fns';
import { useCanCheck } from '@ability/helpers';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getPerformanceNotes, createPerformanceNote } from '../services/performance.service';
import { getEmployees } from '../../employee/services/employee.service';
import { getPerformanceColumns } from '../columns/performanceColumns';

export const PERFORMANCE_NOTES_QUERY_KEY = (userId) => ['hr', 'performance', userId];

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return dateStr;
        return format(d, 'do MMM yyyy');
    } catch {
        return dateStr;
    }
};

export const usePerformance = () => {
    const queryClient = useQueryClient();
    const [createForm] = Form.useForm();
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [createModalOpen, setCreateModalOpen] = useState(false);

    const can = useCanCheck();
    const canView = can('hr.performance.view');
    const canCreate = can('hr.performance.create');

    const { data: employeesResponse } = useQuery({
        queryKey: ['hr', 'employees', 'list'],
        queryFn: () => getEmployees({ page: 1, limit: 100 }),
        enabled: !!(canView || (createModalOpen && canCreate)),
    });

    const { data: notes = [], isError: notesError } = useQuery({
        queryKey: PERFORMANCE_NOTES_QUERY_KEY(selectedUserId),
        queryFn: () => getPerformanceNotes(selectedUserId),
        enabled: !!selectedUserId && !!canView,
        retry: false,
    });

    const notesList = Array.isArray(notes) ? notes : [];

    const createNoteMutation = useMutation({
        mutationFn: (body) => createPerformanceNote(body),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: PERFORMANCE_NOTES_QUERY_KEY(variables.userId) });
        },
    });

    const employeeOptions = useMemo(() => {
        const data = employeesResponse?.data ?? [];
        return (Array.isArray(data) ? data : []).map((e) => ({
            value: e.id,
            label: e.name ? `${e.name} (${e.email})` : e.email,
        }));
    }, [employeesResponse]);

    const tableData = useMemo(
        () =>
            notesList.map((n) => {
                const start = n.startDate ?? n.start_date;
                const end = n.endDate ?? n.end_date;
                const metrics = n.metrics || {};
                const user = n.user || {};
                return {
                    key: n.id,
                    id: n.id,
                    note: n.note,
                    rating: n.rating,
                    createdAt: n.createdAt ?? n.created_at,
                    createdBy: n.createdBy ?? n.created_by,
                    periodStart: start,
                    periodEnd: end,
                    metrics,
                    userName: user.name ?? null,
                    userEmail: user.email ?? null,
                };
            }),
        [notesList],
    );

    const closeCreateModal = useCallback(() => {
        setCreateModalOpen(false);
        createForm.resetFields();
    }, [createForm]);

    const openCreateModal = useCallback(() => {
        setCreateModalOpen(true);
        if (selectedUserId) {
            createForm.setFieldsValue({ userId: selectedUserId });
        }
    }, [selectedUserId, createForm]);

    const onSubmitCreate = useCallback(() => {
        if (!canCreate) return;
        createForm.validateFields().then((values) => {
            const [start, end] = values.period || [];
            const payload = {
                userId: values.userId,
                startDate: start && start.format ? start.format('YYYY-MM-DD') : undefined,
                endDate: end && end.format ? end.format('YYYY-MM-DD') : undefined,
                note: values.note?.trim(),
                metrics: {
                    project_deliveries: values.project_deliveries,
                    attendance: values.attendanceMetric,
                    behaviour: values.behaviour,
                    extra_caricullar_activities: values.extra_caricullar_activities,
                },
            };
            if (!payload.userId || !payload.note || !payload.startDate || !payload.endDate) {
                message.error('Employee, period, and note are required.');
                return;
            }
            createNoteMutation.mutate(payload, {
                onSuccess: () => {
                    message.success('Performance note added.');
                    closeCreateModal();
                },
                onError: (err) => {
                    message.error(err?.response?.data?.message || 'Failed to add note.');
                },
            });
        });
    }, [canCreate, createForm, createNoteMutation, closeCreateModal]);

    const columns = useMemo(
        () => getPerformanceColumns(),
        [],
    );

    const downloadPdf = useCallback(
        (employeeLabel) => {
            if (!selectedUserId || !tableData.length) {
                message.error('Select an employee with performance notes before downloading.');
                return;
            }

            const doc = new jsPDF();
            const title = `Performance Report - ${employeeLabel || ''}`.trim();
            doc.setFontSize(14);
            doc.text(title, 14, 16);

            // Derive employee name/email from label or first row
            const firstRow = tableData[0];
            const label = employeeLabel || '';
            const namePart = label.split('(')[0].trim() || firstRow.userName || 'Employee';
            const emailPart =
                (label.includes('(') && label.split('(')[1]?.replace(')', '').trim()) ||
                firstRow.userEmail ||
                '—';

            // Top summary table: employee info
            autoTable(doc, {
                head: [['Employee', 'Email']],
                body: [[namePart || '—', emailPart || '—']],
                startY: 22,
                styles: { fontSize: 9, cellPadding: 3 },
                headStyles: { fillColor: [51, 51, 51] },
            });

            let currentY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : 32;

            // One details table per note
            tableData.forEach((row, index) => {
                const metrics = [];
                const m = row.metrics || {};
                if (m.project_deliveries != null) metrics.push(`Projects ${m.project_deliveries}%`);
                if (m.attendance != null) metrics.push(`Attendance ${m.attendance}%`);
                if (m.behaviour != null) metrics.push(`Behaviour ${m.behaviour}%`);
                if (m.extra_caricullar_activities != null)
                    metrics.push(`Extra ${m.extra_caricullar_activities}%`);
                const metricText = metrics.join('\n') || '—';

                const start = row.periodStart ? formatDate(row.periodStart) : '—';
                const end = row.periodEnd ? formatDate(row.periodEnd) : '—';
                const period = `${start} - ${end}`;
                const created = row.createdAt ? formatDate(row.createdAt) : '—';
                const rating = row.rating != null ? `${row.rating}/5` : '—';

                autoTable(doc, {
                    body: [
                        ['#', `${index + 1}`],
                        ['Period', period],
                        ['Note', row.note || '—'],
                        ['Metrics', metricText],
                        ['Rating', rating],
                        ['Created', created],
                    ],
                    startY: currentY,
                    styles: { fontSize: 9, cellPadding: 2 },
                    columnStyles: {
                        0: { fontStyle: 'bold', cellWidth: 35 },
                        1: { cellWidth: 'auto' },
                    },
                    theme: 'grid',
                });

                currentY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : currentY + 40;
            });

            // File name: pr_{{name}}_{{createdAt}}.pdf
            const filenameNameSafe = (namePart || 'employee').replace(/\s+/g, '_');
            const createdForFilenameRaw = firstRow.createdAt
                ? format(new Date(firstRow.createdAt), 'yyyyMMdd')
                : 'unknown';
            const filename = `pr_${filenameNameSafe}_${createdForFilenameRaw}.pdf`;

            doc.save(filename);
        },
        [selectedUserId, tableData],
    );

    return {
        selectedUserId,
        setSelectedUserId,
        notesList,
        tableData,
        columns,
        canView,
        canCreate,
        createModalOpen,
        setCreateModalOpen,
        openCreateModal,
        createForm,
        onSubmitCreate,
        closeCreateModal,
        createNotePending: createNoteMutation.isPending,
        employeeOptions,
        notesError,
        formatDate,
        employeeOptionsLoading: !employeesResponse,
        downloadPdf,
    };
};
